// =============================================================================
// Rostering / Planning API
// =============================================================================
import { apiClient } from './http';
import { getTeamCalendar } from './vacations';
import type { Worksite } from './worksites';

/**
 * Espejo de `PlannedShiftResponse` (backend/app/api/v1/endpoints/rostering.py).
 *
 * ⚠️ Este fichero describía un backend imaginario en tres sitios a la vez, y
 * la pantalla del cuadrante no cargaba NUNCA (auditoría ronda 2, bloque 21).
 * Los nombres eran `shift_date`, `start_time`, `end_time`, `planned_hours`; el
 * backend manda `date`, `expected_start_time`, `expected_end_time`,
 * `expected_hours`.
 */
export interface PlannedShift {
  id: number;
  user_id: number;
  user_name: string | null;
  worksite_id: number;
  worksite_name: string | null;
  worksite_code: string | null;
  date: string;
  expected_start_time: string | null;
  expected_end_time: string | null;
  expected_hours: number;
  is_confirmed: boolean;
  notes: string | null;
}

export interface DocumentWarning {
  document_type: string;
  status: string;
  expires_at: string | null;
  days_until_expiry: number | null;
  is_blocking: boolean;
}

/**
 * Espejo de `MyPlanningItem`. El endpoint devuelve una LISTA plana.
 *
 * El cliente lo tipaba como un objeto con `today_shift`, `tomorrow_shift`,
 * `document_status` y `current_worksite` — nada de eso existe. `planning
 * ?.today_shift` sobre un array es siempre `undefined`, así que el banner
 * "Turno de hoy" no se pintaba nunca: sin error, sin log, y el trabajador
 * creyendo que no tiene turno asignado.
 */
export interface MyPlanningItem {
  date: string;
  shift_id: number;
  worksite: { id: number; name: string; code: string } | Record<string, unknown>;
  assigned_task: Record<string, unknown> | null;
  expected_hours: number;
  notes: string | null;
  document_warnings: string[];
}

export async function getMyPlanning(): Promise<MyPlanningItem[]> {
  const response = await apiClient.get<MyPlanningItem[]>('/rostering/my-planning');
  return response.data;
}

/**
 * Celda del cuadrante semanal: UN turno por usuario y día (objeto o null),
 * tal cual lo manda `get_week_calendar`. Ojo: es `shift_id`, no `id`, y no
 * es una lista — el planner adapta esta forma a la suya.
 */
export interface WeekCalendarCell {
  shift_id: number;
  worksite_id: number;
  worksite_code: string | null;
  worksite_name: string | null;
  expected_start_time: string | null;
  expected_end_time: string | null;
  expected_hours: number;
  notes: string | null;
}

/**
 * Espejo de `WeekCalendar`. `days` es una lista de FECHAS (strings) y los
 * turnos van dentro de cada usuario, no dentro del día. El usuario se
 * identifica por `user_id` (la ronda 2 arregló el día y la celda pero el
 * identificador seguía mintiendo: `u.id` no existe y el cuadrante salía
 * vacío aunque hubiera turnos — ronda 3, r2b06/r2b21).
 */
export interface WeekCalendarResponse {
  week_start: string;
  week_end: string;
  days: string[];
  users: {
    user_id: number;
    user_name: string;
    employee_code: string | null;
    days: Record<string, WeekCalendarCell | null>;
  }[];
  worksites: { id: number; name: string; code: string }[];
}

export async function getWeekCalendar(weekStart?: string, worksiteId?: number): Promise<WeekCalendarResponse> {
  const params = new URLSearchParams();
  if (weekStart) params.append('week_start', weekStart);
  if (worksiteId) params.append('worksite_id', worksiteId.toString());

  const response = await apiClient.get<WeekCalendarResponse>(`/rostering/calendar?${params.toString()}`);
  return response.data;
}

/**
 * Espejo de `PlannedShiftCreate`.
 *
 * Se mandaba `{shift_date, start_time, end_time}` y el schema exige `date`
 * (obligatorio, sin default) y lee `expected_start_time`/`expected_end_time`:
 * 422 en toda creación de turno, y las horas que el admin escribía en el modal
 * no llegaban a ningún sitio ni con otro nombre.
 */
export interface CreateShiftRequest {
  user_id: number;
  worksite_id: number;
  date: string;
  expected_start_time?: string;
  expected_end_time?: string;
  expected_hours?: number;
  notes?: string;
}

export async function createShift(data: CreateShiftRequest): Promise<PlannedShift> {
  const response = await apiClient.post<PlannedShift>('/rostering/shifts', data);
  return response.data;
}

export async function deleteShift(shiftId: number): Promise<void> {
  await apiClient.delete(`/rostering/shifts/${shiftId}`);
}

// =============================================================================
// Planificación de recursos (empleados y centros para el cuadrante)
// =============================================================================

/**
 * La plantilla para el cuadrante.
 *
 * Nada de esto se fabrica (ronda 3, r2b21[8]): `document_status` es `null`
 * cuando no hay dato real — la UI no pinta el semáforo—; `vacation_dates`
 * salen del team-calendar de vacaciones (sólo APROBADAS) y
 * `weekly_hours_assigned` la calcula el planificador sumando los turnos del
 * propio calendario de la semana.
 */
export interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  role: string;
  category?: string;
  /** Horas ya asignadas en la semana visible; null si aún no se ha calculado. */
  weekly_hours_assigned: number | null;
  /** Días de vacaciones APROBADAS dentro de la semana visible. */
  vacation_dates: string[];
  /** Estado documental PRL; null = sin dato (no se pinta nada). */
  document_status: 'GREEN' | 'YELLOW' | 'RED' | null;
}

export interface WorksiteForPlanning {
  id: number;
  name: string;
  code: string;
  color?: string;
  is_active: boolean;
  /** Plantilla mínima del centro (el `min_staff` del backend); null = sin dato. */
  required_workers: number | null;
  /** Personas con turno en el centro esta semana; null = sin dato aún. */
  assigned_workers: number | null;
}

/** Aritmética de calendario pura en UTC (nunca toISOString con fecha local). */
function sumarDiasIso(iso: string, dias: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + dias)).toISOString().slice(0, 10);
}

/**
 * La plantilla para el cuadrante (GET /rostering/plantilla).
 *
 * ⚠️ Historia de la pantalla muerta, en dos tandas: primero llamaba a
 * `GET /admin/employees/planning`, una ruta que NUNCA existió —no hay
 * ningún prefijo `/admin/*` en el backend—. El relevo fue `GET /users`,
 * que existe pero es sólo ADMIN: para un MANAGER —rol con el que el menú,
 * la ruta y el resto de endpoints /rostering/* dejan entrar al cuadrante—
 * era un 403 que, al ir en el Promise.all de la carga, tiraba las tres
 * cargas (ronda 4, r2b21[1]). El endpoint nuevo sirve exactamente lo que
 * aquí se consume, con el mismo rol que el cuadrante; las vacaciones
 * aprobadas de la semana salen de `GET /vacations/team-calendar`. Antes se
 * rellenaban `has_vacation: false`, `document_status: 'GREEN'` y las horas
 * de contrato como asignadas: tres indicadores inventados con aspecto de
 * ciertos.
 */
export interface PlantillaItem {
  id: number;
  first_name: string;
  last_name: string;
  // Un peón de obra no tiene correo (ver `AdminUser`).
  email: string | null;
  role: string;
  is_active: boolean;
}

export async function getEmployeesForPlanning(weekStart?: string): Promise<Employee[]> {
  const response = await apiClient.get<PlantillaItem[]>('/rostering/plantilla');

  // Vacaciones APROBADAS de la semana visible: dato real. Si el
  // team-calendar falla, sin dato la UI simplemente no raya la celda —
  // nunca se inventa un «sin vacaciones».
  const vacaciones = new Map<number, string[]>();
  if (weekStart) {
    try {
      const weekEnd = sumarDiasIso(weekStart, 6);
      const dias = await getTeamCalendar(weekStart, weekEnd);
      for (const dia of dias) {
        for (const ausencia of dia.absent) {
          const fechas = vacaciones.get(ausencia.user_id) ?? [];
          fechas.push(dia.date);
          vacaciones.set(ausencia.user_id, fechas);
        }
      }
    } catch (error) {
      console.warn('Sin datos de vacaciones para el cuadrante:', error);
    }
  }

  return response.data
    .filter((u) => u.is_active)
    .map((u) => ({
      id: u.id,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      role: u.role,
      // Sin dato real todavía: el planificador lo rellena sumando los turnos
      // del propio calendario de la semana.
      weekly_hours_assigned: null,
      vacation_dates: vacaciones.get(u.id) ?? [],
      // Sin endpoint de PRL consumido aquí: null = la UI no pinta el punto.
      document_status: null,
    }));
}

export async function getWorksitesForPlanning(): Promise<WorksiteForPlanning[]> {
  // `GET /worksites` NO trae `required_workers`/`assigned_workers`: el tipo
  // los declaraba y la tarjeta del planificador pintaba «undefined/undefined
  // asignados» en todas las empresas (ronda 3, r2b21[10]). La plantilla mínima
  // sí existe y se mapea de `min_staff`; los asignados los cuenta el
  // planificador con los turnos de la semana visible.
  const response = await apiClient.get<Worksite[]>('/worksites');
  return response.data.map((w) => ({
    id: w.id,
    name: w.name,
    code: w.code,
    is_active: w.is_active,
    required_workers: w.min_staff ?? null,
    assigned_workers: null,
  }));
}

/**
 * Copiar los turnos de una semana a otra (POST /rostering/copy-week).
 *
 * El endpoint existía desde siempre y «Copiar semana» era un botón sin
 * handler: nada, ni siquiera un error (ronda 3, r2b21[9]).
 */
export interface CopyWeekRequest {
  source_week_start: string;
  target_week_start: string;
}

export async function copyWeek(data: CopyWeekRequest): Promise<PlannedShift[]> {
  const response = await apiClient.post<PlannedShift[]>('/rostering/copy-week', data);
  return response.data;
}

// (Bloques 'Punch Corrections', 'Audit Log' y 'Dashboard Stats' eliminados:
// llamaban a endpoints /admin/* que nunca existieron en el backend. Las
// pantallas se reescribieron contra /corrections, /audit y contadores reales.)
